import * as m from '@staiter/i18n';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '~/components/data';
import { Button, DatePicker } from '~/components/primitives';
import { DEFAULT_LANDING_ROUTE } from '~/lib/rbac';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.onboarding.module.css';

type Step = 'welcome' | 'integrations' | 'opening-stock' | 'summary';
const ORDER: Step[] = ['welcome', 'integrations', 'opening-stock', 'summary'];

export const Route = createFileRoute('/_auth/onboarding/$step')({
  component: OnboardingWizard,
});

function OnboardingWizard(): JSX.Element {
  const { step } = useParams({ from: '/_auth/onboarding/$step' }) as { step: Step };
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [openingDate, setOpeningDate] = useState(new Date().toISOString().slice(0, 10));

  const idx = ORDER.indexOf(step);
  const safeIdx = idx === -1 ? 0 : idx;
  const current = ORDER[safeIdx]!;

  function go(next: Step): void {
    navigate({ to: '/onboarding/$step', params: { step: next } });
  }

  return (
    <div className={styles.wrap}>
      <PageHeader
        title={m.route_onboarding_title()}
        description={m.route_onboarding_description()}
      />
      <ol className={styles.stepper}>
        {ORDER.map((s, i) => (
          <li
            key={s}
            className={[
              styles.step,
              i === safeIdx ? styles.stepActive : '',
              i < safeIdx ? styles.stepDone : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className={styles.stepNum}>{i + 1}</span>
            <span>{m.route_onboarding_step_label({ step: s })}</span>
          </li>
        ))}
      </ol>

      <section className={styles.panel}>
        {current === 'welcome' && (
          <>
            <h2>
              {m.route_onboarding_welcome_heading({
                name: user?.full_name ?? m.route_onboarding_welcome_fallback_name(),
              })}
            </h2>
            <p>{m.route_onboarding_welcome_p1()}</p>
            <p>{m.route_onboarding_welcome_p2()}</p>
          </>
        )}
        {current === 'integrations' && (
          <>
            <h2>{m.route_onboarding_integrations_heading()}</h2>
            <p>{m.route_onboarding_integrations_p()}</p>
            <Link to="/admin" className={styles.link}>
              {m.route_onboarding_integrations_link()}
            </Link>
          </>
        )}
        {current === 'opening-stock' && (
          <>
            <h2>{m.route_onboarding_opening_heading()}</h2>
            <p>{m.route_onboarding_opening_p()}</p>
            <DatePicker
              label={m.route_onboarding_opening_date_label()}
              required
              value={openingDate || null}
              onChange={(v) => setOpeningDate(v ?? '')}
              hint={m.route_onboarding_opening_date_hint()}
            />
            <p className={styles.muted}>{m.route_onboarding_opening_footnote()}</p>
          </>
        )}
        {current === 'summary' && (
          <>
            <h2>{m.route_onboarding_summary_heading()}</h2>
            <p>{m.route_onboarding_summary_p()}</p>
            <Link to="/" className={styles.link}>
              {m.route_onboarding_summary_link()}
            </Link>
          </>
        )}
      </section>

      <div className={styles.nav}>
        {safeIdx > 0 && (
          <Button variant="ghost" onClick={() => go(ORDER[safeIdx - 1]!)}>
            {m.common_button_back()}
          </Button>
        )}
        <div className={shared.spacerFlex} />
        {safeIdx < ORDER.length - 1 ? (
          <Button onClick={() => go(ORDER[safeIdx + 1]!)}>{m.common_button_next()}</Button>
        ) : (
          <Button onClick={() => navigate({ to: DEFAULT_LANDING_ROUTE as '/' })}>
            {m.route_onboarding_btn_finish()}
          </Button>
        )}
      </div>
    </div>
  );
}
